import { Suspense, lazy } from 'react';
import { Navigate, NavLink, Route, Routes, useLocation } from 'react-router';

import { isStaff } from '@dimensionscore/shared';

import { AudioCompanionProvider } from './audio/AudioCompanionProvider';
import { AudioPage } from './audio/AudioPage';
import { SELF_SERVICE_HOME, StaffRoutes } from './auth/StaffOnly';
import { useSession } from './auth/SessionProvider';
import { AuditPage } from './pages/AuditPage';
import { InspectorPage } from './pages/InspectorPage';
import { PlayerPage } from './pages/PlayerPage';
import { isPhonePath } from './phone/apps';
import { MapApp } from './phone/MapApp';
import { AppScreen, PhoneShell } from './phone/PhoneShell';
import { PhoneHome } from './phone/PhoneHome';
import { SettingsApp } from './phone/SettingsApp';
import { StatsApp } from './phone/StatsApp';
import { EditorPage } from './pages/EditorPage';
import { LinkPage } from './pages/LinkPage';
import { NotFoundPage } from './pages/NotFoundPage';
import { PixelBadge } from './components/PixelBadge';
import { SessionBadge } from './components/SessionBar';
import { ThemeToggle } from './components/ThemeToggle';

/**
 * The form engine (RJSF + ajv) is around three quarters of the bundle and is needed by
 * exactly one route. Read mode is the page people leave open all day, so it must not pay for
 * the editor; splitting here keeps the initial download to the shell and the inspector.
 */
const SchemaFormPage = lazy(async () => ({ default: (await import('./pages/SchemaFormPage')).SchemaFormPage }));

/**
 * Split for the same reason and out of the same chunk: the grant form is the SAME engine
 * rendering the SAME generated schema, so it must not drag RJSF and ajv into the initial
 * download of a read-mode tab that will never file a grant. Vite gives both routes one
 * shared vendor chunk, so the second lazy route costs nothing extra once either is open.
 */
const GrantPage = lazy(async () => ({ default: (await import('./pages/GrantPage')).GrantPage }));

/**
 * The menus surface, split for a different reason from the two above: it needs no form engine
 * at all - it is a grid, a stack editor and an action chain over another mod's JSON - so it
 * must not be bundled WITH the RJSF chunk either. Its own chunk keeps `/editor/menus` off the
 * form engine and the form routes off the menu vocabulary.
 */
const MenusPage = lazy(async () => ({ default: (await import('./pages/MenusPage')).MenusPage }));
const MenuEditorPage = lazy(async () => ({ default: (await import('./pages/MenusPage')).MenuEditorPage }));

/**
 * The application shell: header, navigation, routed outlet, footer.
 *
 * Deliberately thin. Read mode (R2-3) mounts the inspector and the player detail page
 * into this outlet; the schema-driven editor (R4) mounts beside them without touching
 * the shell. The `data-testid` hooks are contract with the Playwright specs, so rename
 * them only together with the specs.
 *
 * IT IS NO LONGER ONE AUDIENCE'S SHELL, WHICH IS THE ONE THING TO KNOW BEFORE EDITING IT. Since the
 * `player` and `unlinked` tiers exist, two of the three things in this file branch on the tier: the
 * nav (`PrimaryNav`, at the bottom) and the route table (`StaffRoutes`, a pathless layout route
 * wrapping every staff page). Neither is a security boundary — a browser that edits its own stored
 * role renders every staff page and reads nothing new, because the sidecar verifies the signed copy
 * of that token — and both exist so that a self-service session is not shown doors that slam.
 */
export function App(): React.JSX.Element {
  /*
   * THE AUDIO COMPANION SITS ABOVE EVERY ROUTE (plan §9.11): the Rotom Phone's apps come and go, the music
   * does not. It does nothing at all until an app that plays or shows the music switches it on.
   */
  return (
    <AudioCompanionProvider>
      <AppShell />
    </AudioCompanionProvider>
  );
}

function AppShell(): React.JSX.Element {
  // The phone is its own device: on its routes there is no site header, footer or column.
  const phone = isPhonePath(useLocation().pathname);
  return (
    <div className={phone ? 'dc-shell dc-shell-phone' : 'dc-shell'} data-testid="app-shell">
      {!phone && (
        <header className="dc-header">
          {/*
            ONE WORDMARK FOR EVERYONE: the server's own name, CobblemonDimensions. The shell is shared by
            two audiences, and a staff word in a player's header tells them they are somewhere they
            should not be — so staff get a small "Staff" tag beside the name instead of a different name.
            The footer below branches the same way, for the same reason.
          */}
          <Brand />
          <PrimaryNav />
          <SessionBadge />
          <ThemeToggle />
        </header>
      )}

      <main className={phone ? 'dc-main dc-main-phone' : 'dc-main'} data-testid="app-main">
        <Routes>
          {/*
            THE ROTOM PHONE (plan §9.11): every player-facing route, inside one layout route, so moving
            between its apps keeps the phone — status bar, notifications — mounted. None is lazy: these
            are the routes a NON-STAFF person is sent to, and none should wait on a chunk.

            `/audio` is the Music app, and the other end of the link `/audio` prints in game. `/me` is
            Stats; it carries no identifier — the account it is about is the `uuid` claim on the signed
            token, so there is nothing in the URL to increment or to share. `/link` is the other end of
            the URL `/link` prints in game (`<origin>/link`, built by the sidecar from PUBLIC_EDITOR_URL),
            and Stats leads there when linking is the only thing left to do.
          */}
          <Route element={<PhoneShell />}>
            <Route path="/phone" element={<PhoneHome />} />
            <Route
              path="/audio"
              element={
                <AppScreen app="music">
                  <AudioPage />
                </AppScreen>
              }
            />
            <Route
              path="/map"
              element={
                <AppScreen app="map">
                  <MapApp />
                </AppScreen>
              }
            />
            <Route
              path="/me"
              element={
                <AppScreen app="stats">
                  <StatsApp />
                </AppScreen>
              }
            />
            <Route
              path="/settings"
              element={
                <AppScreen app="settings">
                  <SettingsApp />
                </AppScreen>
              }
            />
            <Route
              path="/link"
              element={
                <AppScreen app="link" title="Link your account">
                  <LinkPage />
                </AppScreen>
              }
            />
          </Route>

          {/*
            `/audit` IS DELIBERATELY NOT IN THE GUARDED GROUP BELOW, and the difference is worth the
            two lines. It refuses a non-staff tier in its own words — naming the tier it needs and
            saying that the database, not the page, is what enforces it — and that sentence is more
            use to whoever arrives here than a silent bounce to `/me` would be. Nothing of the log
            renders: no table, no rows, and the page issues no read at all. It is absent from the nav
            for a self-service session either way, so the only way here is by typing the URL, which
            is exactly the person the explanation is for.
          */}
          <Route path="/audit" element={<AuditPage />} />

          {/*
            EVERY STAFF ROUTE, GUARDED ONCE, BY BEING INSIDE THIS GROUP.

            `StaffRoutes` is a pathless layout route: a self-service session that lands on anything
            below is sent to `/me` and none of the markup mounts, so "no staff chrome for a player"
            is a property of the route table rather than of nine remembered conditionals. A staff
            route added inside is guarded by having been put here; one added outside is visibly
            outside. Signed out is untouched — each page still draws its own sign-in prompt, which
            issues no requests.
          */}
          <Route element={<StaffRoutes />}>
            <Route path="/" element={<HomeRoute />} />
            <Route path="/player/:uuid" element={<PlayerPage />} />
            {/*
              The web's one write, and it hangs off the player it targets rather than living
              at a top-level `/grant`: the target is a path segment the sidecar reads and
              nothing else names, so there is no such thing as a grant without a player.
            */}
            <Route
              path="/player/:uuid/grant"
              element={
                <Suspense fallback={<p className="dc-dim">Loading the form engine…</p>}>
                  <GrantPage />
                </Suspense>
              }
            />
            <Route path="/editor" element={<EditorPage />} />
            {/*
              One route for every config there will ever be. `:id` is a schema file stem from
              the contract mirror, so a config POJO added later is reachable the moment its
              schema is generated - no route is added here for it. Declared BEFORE
              `/editor/:code` so `schema` is never mistaken for a session code.
            */}
            <Route
              path="/editor/schema/:id"
              element={
                <Suspense fallback={<p className="dc-dim">Loading the form engine…</p>}>
                  <SchemaFormPage />
                </Suspense>
              }
            />
            {/*
              The menus surface. Declared BEFORE `/editor/:code` for the same reason
              `/editor/schema/:id` is: `menus` is not a session code and must never be read as
              one. It has no schema route because it has no schema - the documents belong to
              FabricMenus and arrive in the session bundle, which is what `MenusPage` reads.
            */}
            <Route
              path="/editor/menus"
              element={
                <Suspense fallback={<p className="dc-dim">Loading the menu editor…</p>}>
                  <MenusPage />
                </Suspense>
              }
            />
            <Route
              path="/editor/menus/:menuId"
              element={
                <Suspense fallback={<p className="dc-dim">Loading the menu editor…</p>}>
                  <MenuEditorPage />
                </Suspense>
              }
            />
            {/*
              The URL `/cdim editor` actually prints is `<origin>/editor/<code>` - see
              `sessionEditUrl`. Without this line that link falls through to `*` and an
              admin who clicks a freshly minted session sees "not found", which is
              indistinguishable from a broken deployment.
            */}
            <Route path="/editor/:code" element={<EditorPage />} />
          </Route>

          <Route path="*" element={<NotFoundPage />} />
        </Routes>
      </main>

      {!phone && <ShellFooter />}
    </div>
  );
}

/**
 * The site root, which is TWO pages depending on who is reading (plan §9.2.5, §9.11).
 *
 * Staff keep their console there: the inspector, exactly as before — it is the phone's Console app.
 * Everybody else — a player's session, and NO session at all — lands on the Rotom Phone's home screen,
 * because the visitor most likely to type the bare address is a player. A signed-out member of staff
 * signs in from the phone's staff door, and the sign-in returns them here, now staff, to the inspector.
 */
function HomeRoute(): React.JSX.Element {
  const { session } = useSession();
  if (session === null || !isStaff(session.claims.role)) return <Navigate to={SELF_SERVICE_HOME} replace />;
  return <InspectorPage />;
}

/**
 * Whether this session is a self-service one (a player or an unlinked Discord account).
 *
 * Signed out is NOT self-service: an admin arriving cold at a bookmarked staff URL is an unknown tier
 * rather than a known-narrow one, and must see the tool they bookmarked. (The phone, where the reader is
 * presumed a player, draws no header at all.) Read the consumers below together - nav, brand, footer -
 * they are one decision made in one place.
 */
function useSelfService(): boolean {
  const { session } = useSession();
  return session !== null && !isStaff(session.claims.role);
}

/** Whether the reader holds a staff session — the only thing that earns the staff tag and nav. */
function useStaff(): boolean {
  const { session } = useSession();
  return session !== null && isStaff(session.claims.role);
}

/** The wordmark: the server's name for everyone, with a tag that says so when the reader is staff. */
function Brand(): React.JSX.Element {
  const staff = useStaff();
  return (
    <NavLink to={staff ? '/' : SELF_SERVICE_HOME} className="dc-brand" aria-label={staff ? 'CobblemonDimensions staff console' : 'CobblemonDimensions'} end>
      <PixelBadge className="dc-brand-badge" />
      <span className="dc-brand-word">CobblemonDimensions</span>
      {staff && <span className="dc-brand-tag">Staff</span>}
    </NavLink>
  );
}

/**
 * The footer, which for a staff reader explains the architecture and for a player answers the
 * question they actually have.
 *
 * A player has never heard of PostgREST and has no use for the sentence that names it; what they
 * might reasonably wonder, looking at a web page listing their homes, is who else can see it. The
 * answer is true and worth saying: nobody, and not because this page hides it - the database
 * filters every row to the account the session names (web-roles.sql section 5).
 */
function ShellFooter(): React.JSX.Element {
  if (useSelfService()) {
    return (
      <footer className="dc-footer">
        This page is only yours: the database gives out the rows belonging to the account you signed in with, and nobody else&rsquo;s. It can only
        read them &mdash; the in-game commands are what change anything.
      </footer>
    );
  }
  return (
    <footer className="dc-footer">
      Read mode is served by PostgREST over the read-only <code>web.*</code> views, as a Postgres role whose grants are the real limit on what any
      sign-in can see. The game server never opens an inbound port.
    </footer>
  );
}

/**
 * The header nav, which is one of the two places the tier changes what exists.
 *
 * A SELF-SERVICE SESSION IS OFFERED ONE LINK, AND THE OTHERS ARE NOT RENDERED. Not disabled and not
 * hidden: `Editor` and `Audit` are surfaces the sidecar refuses that tier, and `Inspector` is a
 * search box over other people — so for a player they are three doors that slam. The routes behind
 * them are guarded independently (see `StaffRoutes`), so this is presentation catching up with a
 * decision made elsewhere, not the decision itself.
 *
 * SIGNED OUT KEEPS THE STAFF NAV, because a signed-out visitor is an unknown tier rather than a
 * known-narrow one: an admin arriving cold must be able to reach `/editor` and sign in there. Each
 * of those pages draws a sign-in prompt and issues no requests until it has a session.
 */
function PrimaryNav(): React.JSX.Element {
  const selfService = useSelfService();

  return (
    <nav className="dc-nav" data-testid="app-nav" aria-label="Primary" data-nav-scope={selfService ? 'self' : 'staff'}>
      {selfService ? (
        // A player who typed a staff URL that answers rather than redirects (the audit log's refusal).
        <NavLink to={SELF_SERVICE_HOME} data-testid="nav-phone">
          Rotom Phone
        </NavLink>
      ) : (
        <>
          <NavLink to="/" end>
            Inspector
          </NavLink>
          <NavLink to="/editor">Editor</NavLink>
          <NavLink to="/audit">Audit</NavLink>
          {/* Staff keep their console and get the players' phone beside it (plan §9.11). */}
          <NavLink to={SELF_SERVICE_HOME} data-testid="nav-phone">
            Rotom Phone
          </NavLink>
        </>
      )}
    </nav>
  );
}
